import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import api from '../utils/api'
import { TimeWindow, TimeWindowFilter } from '@/components/TimeWindowFilter'
import { Button } from '@/components/Button'
import { useToast } from '@/components/Toast'
import { apiErrorMessage } from '@/utils/apiErrors'

interface Architecture {
  id: string
  title: string
  summary?: string
  status: string
  document?: Record<string, unknown>
  created_at?: string
}

/**
 * Browse/review architectures drafted by Beacon in chat.
 * Creation happens via Chat tools — this page only lists, View/Hide, Approve, Implement.
 */
export default function BeaconArchitectures() {
  const { toast } = useToast()
  const [window, setWindow] = useState<TimeWindow>('month')
  const [rows, setRows] = useState<Architecture[]>([])
  const [selected, setSelected] = useState<Architecture | null>(null)

  const load = useCallback(async () => {
    try {
      const res = await api.get('/api/v1/beacon/architectures', { params: { window } })
      setRows(res.data.architectures || [])
    } catch (err: any) {
      toast.error('Failed to load architectures', apiErrorMessage(err))
    }
  }, [window, toast])

  useEffect(() => { load() }, [load])

  const approve = async (id: string) => {
    try {
      await api.post(`/api/v1/beacon/architectures/${id}/approve`)
      toast.success('Approved')
      await load()
    } catch (err: any) {
      toast.error('Approve failed', apiErrorMessage(err))
    }
  }

  const implement = async (id: string) => {
    try {
      const res = await api.post(`/api/v1/beacon/architectures/${id}/implement`, { dry_run: true })
      toast.success('Implement', res.data.status)
      await load()
    } catch (err: any) {
      toast.error('Implement failed', apiErrorMessage(err))
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl text-slate-900">Architectures</h2>
          <p className="text-sm text-slate-600">
            Review drafts Beacon created in chat — then approve and implement (dry-run).
          </p>
        </div>
        <TimeWindowFilter value={window} onChange={setWindow} />
      </div>

      {rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-10 text-center">
          <p className="text-sm text-slate-700">
            No architectures yet. Ask Beacon in{' '}
            <Link to="/" className="font-medium text-teal-700 underline underline-offset-2 hover:text-teal-800">
              Chat
            </Link>{' '}
            to design or build something — drafts show up here for review.
          </p>
        </div>
      ) : (
        <div className="grid gap-3 lg:grid-cols-2">
          {rows.map((a) => (
            <div key={a.id} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="font-medium text-slate-900">{a.title}</h3>
                  <p className="mt-1 text-xs text-slate-500">{a.summary}</p>
                </div>
                <span className="rounded-full bg-teal-50 px-2 py-0.5 text-xs text-teal-800">{a.status}</span>
              </div>
              <div className="mt-3 flex gap-2">
                <Button
                  variant="secondary"
                  onClick={() => setSelected((prev) => (prev?.id === a.id ? null : a))}
                >
                  {selected?.id === a.id ? 'Hide' : 'View'}
                </Button>
                {a.status === 'draft' && <Button onClick={() => approve(a.id)}>Approve</Button>}
                {(a.status === 'approved' || a.status === 'implemented') && (
                  <Button variant="secondary" onClick={() => implement(a.id)}>Implement (dry-run)</Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {selected && (
        <pre className="overflow-auto rounded-xl border border-slate-200 bg-slate-900 p-4 text-xs text-slate-100">
          {JSON.stringify(selected.document || selected, null, 2)}
        </pre>
      )}
    </div>
  )
}
